// src/pages/esim/TopupCarousel.jsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import './TopupCarousel.css';
import { useLanguage } from '../../i18n/LanguageContext';

const TopupCarousel = ({ regionCode, iccid, SimId }) => {
    const [topups, setTopups] = useState([]);
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useLanguage();

    const formatIDR = (v) =>
        new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            maximumFractionDigits: 0,
        }).format(v);

    // Defer fetch until after initial page is done rendering
    useEffect(() => {
        const ctrl = new AbortController();
        const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 0));
        const cancelIdle = window.cancelIdleCallback || clearTimeout;

        const handle = idle(async () => {
            setLoading(true);
            try {
                const res = await fetch(`https://api.artamobile.id/v1/product/topup/${SimId}`, {
                    signal: ctrl.signal,
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();

                const list = (data?.productList || [])
                    .map((p) => ({
                        ...p,
                        validity: parseInt(p.validity, 10),
                        gb:
                            typeof p.gb === 'number'
                                ? p.gb
                                : parseInt(String(p.balance).replace(/\D/g, ''), 10),
                    }))
                    .sort((a, b) => a.gb - b.gb);

                if (!ctrl.signal.aborted) setTopups(list);
            } catch (e) {
                console.error('Failed to fetch topups', e);
            } finally {
                setLoading(false);
            }
        });

        return () => cancelIdle(handle);
    }, [regionCode, SimId]);

    return (
        <section className="topup-section">
            <div className="topup-header">
                <h3>{t('esimLegacy.topupHeader')}</h3>
                <ShoppingCart size={22} />
            </div>

            {loading ? (
                <div className="topup-skeleton">
                    <div className="topup-card skeleton" />
                    <div className="topup-card skeleton" />
                    <div className="topup-card skeleton" />
                </div>
            ) : (
                <div className="topup-carousel" aria-label={t('esimLegacy.topupAria')}>
                    {topups.map((pkg) => (
                        <div className="topup-card" key={pkg.code}>
                            <div className="topup-balance">{pkg.balance}</div>
                            <div className="topup-meta">
                                <span>{t('esimLegacy.days', { n: pkg.validity })}</span>
                                <span className="dot">•</span>
                                <span>{formatIDR(pkg.price)}</span>
                            </div>
                            <button
                                className="topup-cta"
                                onClick={() => {
                                    if (!SimId) {
                                        alert(t('common.genericError'));
                                        return;
                                    }
                                    const params = new URLSearchParams(location.search);
                                    params.set('topup', '1');
                                    params.set('iccid', iccid);
                                    params.set('SimId', SimId);
                                    navigate(`/product/${pkg.code}?${params.toString()}`);
                                }}
                            >
                                {t('esimLegacy.topupCta')}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};

export default TopupCarousel;
